import * as React from "react"
import {Button} from "@/components/ui/button"
import {Toggle} from "@/components/ui/toggle"
import {cn} from "@/lib/utils"
import {formatCompactNumber} from "@/main/util/LocalizationUtil"

const agreeActive = "!fill-agree !text-agree !stroke-agree  "
const disagreeActive = "!fill-disagree !text-disagree !stroke-disagree  "
const defaultActive = "!fill-action !text-action !stroke-action"
const primarySolidActive = "!border-[var(--primary-action-hover)] !bg-[var(--primary-action-hover)] !text-[var(--background1)] !stroke-[var(--background1)]"
/*
 * `disabled:opacity-100` is load-bearing, not redundant.
 *
 * This class already states a COMPLETE disabled treatment — muted text on a muted fill, no border, no
 * shadow — but every CtaButton renders through `Button`, whose shadcn base carries a blanket
 * `disabled:opacity-50`. That halves an already-muted token instead of replacing a saturated one:
 * `--text-muted` (gray-500 #6a7282) at 50% over `--muted` computes to ≈#b1b5be, about **2.1:1**, well
 * under the 4.5:1 floor. So the more carefully a disabled state was chosen here, the worse it rendered.
 *
 * It surfaced on 2026-07-30 when the group board composer's hand-rolled pill became a CtaButton and its
 * disabled "Post"/"Reply" label fell from ~4.3:1 to ~2.1:1 — but nothing about it was specific to that
 * button. Every disabled CTA in the app had it.
 *
 * `disabled:border-border` is the OTHER half, and it is not a restatement of the resting border.
 *
 * Until 2026-08-20 the disabled state read `disabled:border-transparent`, so the pill lost its edge and
 * kept only a `--muted` fill — a mix off the same ramp as `--card`. Measured on the captured signup and
 * reset frames that fill is 1.05-1.11:1 against the card in both themes: the container vanished and only
 * the (correctly legible) label floated, which is why a disabled primary read as a broken page rather
 * than as a control waiting for input. ux-critique `ACCOUNT-ACCESS-2`, filed independently on four
 * account-access screens.
 *
 * `--border` and not `--control-boundary`. Both clear WCAG 1.4.11's 3:1 floor against `--card` and
 * `--background`, so this is a WEIGHT choice, not a contrast one: `--border` measures 3.39-3.91:1 light
 * and 3.23-4.16:1 dark, `--control-boundary` 4.46:1 light and 4.01:1 dark on card (both values quoted
 * from scripts/check-control-boundary-tokens.mjs, which pins them — do not re-derive them by eye).
 * Taking the heavier token would have given the DISABLED pill a stronger edge than the ENABLED `outline`
 * variant, which paints `border-border`. Enabled and disabled must not invert.
 */
const ctaBaseClass = "cursor-pointer border flex text-center justify-center items-center content-center gap-1 whitespace-nowrap min-w-max transition-[background-color,border-color,color,box-shadow,transform] disabled:text-muted-foreground disabled:bg-muted disabled:border-border disabled:shadow-none disabled:opacity-100 disabled:hover:cursor-not-allowed"

export type CtaSize = "sm" | "md" | "lg"
export type CtaVariant = "ghost" | "primary" | "secondary" | "outline" | "soft" | "danger" | "destructive" | "agree" | "disagree" | "link"
export type CtaInteractionEffect = "none" | "weight" | "follow" | "bookmark" | "agree" | "disagree"
export type CtaCountFeedback = "none" | "pulse"
export type CtaTone = "default" | "muted"

const ACTIVE_MAP: Record<string, string> = {
    "support-discussion-button": agreeActive,
    "agreement-discussion-button": agreeActive,
    "upvote-thought-button": agreeActive,
    "oppose-discussion-button": disagreeActive,
    "disagreement-discussion-button": disagreeActive,
    "downvote-thought-button": disagreeActive,
    "follow-discussion-button": defaultActive,
    "follow-user-button": defaultActive,
    "follow-argument-button": defaultActive,
    "cta-btn-dropdown": defaultActive,
    "join-group-button": defaultActive,
    "group-join-button": defaultActive,
    "side_bar_conversation__btn": defaultActive,
    "series-modal-entry": defaultActive,
    "groups-modal-entry": defaultActive,
}

/**
 * Dev-time invariant for the accessible name of an icon-only CTA.
 *
 * An icon-only CTA exposes NOTHING to assistive tech unless the call site names it: `CtaButtonIcon`
 * renders the glyph `aria-hidden`, both reserve snapshots in `CtaButtonContent` are `aria-hidden`,
 * and the `title` `CtaButton` emits is derived from `text` — which is null by definition here. Five
 * such controls shipped, four of them the `…` menu holding report / block / leave.
 *
 * It LOGS, and deliberately does not throw. These primitives render inside React islands mounted with
 * `createRoot()` over server-rendered HTML, which discards the element's children, so a throw during
 * render leaves the page with LESS than the plain HTML already contained (AGENTS.md rule 32). A
 * missing label is not worth a blank action row. The build-time half of the obligation is the source
 * sweep in `ctaAccessibleName.contract.test.tsx`, which fails the frontend build instead.
 *
 * Silent in production: the message is for whoever is adding the control, and one console.error per
 * card on an infinite list would be its own defect.
 */
export function warnIfCtaHasNoAccessibleName(
    componentName: string,
    {text, ariaLabel, title, ariaLabelledBy}: {
        text?: string | null
        ariaLabel?: string | null
        title?: unknown
        ariaLabelledBy?: unknown
    },
): void {
    if (process.env.NODE_ENV === "production") {
        return
    }
    if (text != null || ariaLabel != null || title != null || ariaLabelledBy != null) {
        return
    }
    // eslint-disable-next-line no-console
    console.error(
        `${componentName}: an icon-only CTA (text is null) rendered with no accessible name. Screen readers ` +
        `announce it as an unlabelled button. Pass ariaLabel={localizer(<an EXISTING catalog key>)}, or ` +
        `aria-labelledby to compose the name from text already on the page. Do not mint new copy for it ` +
        `(AGENTS.md rule 11).`,
    )
}

export function getMappedActiveClass(extraClass?: string | null) {
    if (!extraClass) return undefined

    const matchedClass = extraClass
        .split(/\s+/)
        .map((value) => value.trim())
        .find((value) => Boolean(ACTIVE_MAP[value]))

    return matchedClass ? ACTIVE_MAP[matchedClass] : undefined
}

export function resolveActiveClass(buttonActive: boolean, variant: CtaVariant, activeClass?: string, extraClass?: string | null) {
    if (!buttonActive) {
        return ""
    }

    if (activeClass) {
        return activeClass
    }

    const mappedActiveClass = getMappedActiveClass(extraClass)

    if (variant === "primary" && (!mappedActiveClass || mappedActiveClass === defaultActive)) {
        return primarySolidActive
    }

    return mappedActiveClass ?? defaultActive
}

/**
 * Renders a count inside a compact icon CTA with a BOUNDED width: exact below 1000, abbreviated above
 * (`1.2K`, `-1.2K`). Pair with `tabular-nums` at the call site so the glyph width is stable too.
 *
 * Why bounded: the discussion action row is a `flex-wrap` row of 44px tap targets that on a ~390px
 * phone has ~15px of slack. An unbounded count widens it without limit and wraps the `…` overflow
 * button onto its own line. Tests read the exact value off `data-cta-count`, never
 * off this text. See
 * designs/01-now/2026-07-29-mobile-avatar-load-resilience-and-card-action-row-fit-design.md.
 */
export const CTA_COUNT_EXACT_LIMIT = 1000

export function formatCtaCount(count: number): string {
    if (!Number.isFinite(count)) {
        return "0"
    }

    const rounded = Math.trunc(count)
    if (Math.abs(rounded) < CTA_COUNT_EXACT_LIMIT) {
        return String(rounded)
    }

    try {
        // The APP's locale, not the browser's (front-end F41, 2026-08-12). This called
        // `new Intl.NumberFormat(undefined, …)` until then, so the count followed whatever the OS was
        // set to while every count rendered through a message catalog followed the language the member
        // actually chose — two groupings on one screen.
        //
        // One decimal only in the 1K–9.9K band; above that the decimal is noise and would push the
        // English label to 5 characters ("12.3K"), which is the width this function exists to cap.
        // Note that cap is an ENGLISH property: `fr` renders "12 k" and `de` has no short thousands
        // form at all. That was already true of the browser-locale call this replaces.
        return formatCompactNumber(rounded, Math.abs(rounded) < 10 * CTA_COUNT_EXACT_LIMIT ? 1 : 0)
    } catch {
        // Intl unavailable/unsupported notation — degrade to the exact value rather than to nothing.
        return String(rounded)
    }
}

export function getCtaSizePaddingClass(size: CtaSize) {
    return size === "sm" ? "px-2.5 text-xs" : size === "lg" ? "px-4 text-base" : "px-3 text-sm"
}

export function getCtaSizeHeightClass(size: CtaSize) {
    // sm is a compact-but-confident 32px resting pill (still clearly below md's 40px). Icons scale
    // to 18px in getCtaIconBaseClass so the glyph doesn't read as timid at this height.
    return size === "sm" ? "h-8" : size === "lg" ? "h-12" : "h-10"
}

export function getCtaIconBaseClass(size: CtaSize) {
    return size === "sm"
        ? "fill-none stroke-2 h-4.5 w-4.5"
        : size === "lg"
            ? "fill-none stroke-2 h-7 w-7"
            : "fill-none stroke-2 h-6 w-6"
}

/*
 * Touch-only tap-target floor. On a coarse pointer (phone/tablet, touchscreen laptop) the dense
 * sm/md pills are lifted to the 44px WCAG/Apple/Material minimum without affecting a mouse pointer —
 * we key off the input device (@media(pointer:coarse)), never a viewport breakpoint, so a small
 * desktop window is never enlarged. lg (48px) already clears 44px, so it only gets touch-manipulation
 * (removes the 300ms tap delay / double-tap-zoom). Mirrors HeaderButton's min-h-11/min-w-11 target,
 * made conditional. Baked into getCtaClasses so every CTA — including bare VoteContainer arrows —
 * inherits it consistently.
 */
export function getCtaSizeTouchTargetClass(size: CtaSize) {
    return size === "lg"
        ? "touch-manipulation"
        : "touch-manipulation [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
}

export function getBaseButtonVariant(variant: CtaVariant): React.ComponentProps<typeof Button>["variant"] {
    switch (variant) {
        case "primary":
            return "default"
        case "secondary":
            return "secondary"
        case "outline":
            return "outline"
        case "destructive":
            return "destructive"
        // `outline`, not `destructive`: the quiet tier paints its own tinted surface, and starting from
        // the solid destructive base would leave its border and hover behind the tint.
        case "danger":
            return "outline"
        case "link":
            return "link"
        default:
            return "ghost"
    }
}

export function getBaseToggleVariant(variant: CtaVariant): React.ComponentProps<typeof Toggle>["variant"] {
    if (variant === "outline") {
        return "outline";
    }
    if (variant === "primary" || variant === "secondary" || variant === "soft") {
        return "default";
    }
    return "ghost";
}

/*
 * One button vocabulary. These mirror buttonVariants() in components/ui/button.tsx
 * so a CtaButton and a <Button> of the same variant read identically: shadcn semantic
 * tokens, the same hover treatment, flat (no resting shadow), pill geometry from
 * getCtaClasses. Coloured agree/disagree/soft variants use the accent token at low
 * opacity for a calm tinted pill rather than a heavy color-mixed surface.
 */
export function getCtaVariantClasses(variant: CtaVariant) {
    switch (variant) {
        case "primary":
            return "border-transparent bg-primary text-primary-foreground shadow-none hover:bg-primary/90"
        case "secondary":
            return "border-transparent bg-muted text-secondary-foreground shadow-none hover:bg-accent"
        case "outline":
            return "border-border bg-card text-foreground shadow-none hover:bg-muted hover:text-foreground"
        case "soft":
            return "border-transparent bg-primary/10 text-foreground shadow-none hover:bg-primary/15"
        case "destructive":
            return "border-transparent bg-destructive text-destructive-foreground shadow-none hover:bg-destructive/90"
        /*
         * The QUIET destructive tier — same calm-tinted-pill construction as agree/disagree above.
         *
         * `danger` and `destructive` returned the identical solid fill until 2026-07-31, which meant
         * the vocabulary had no way to say "this is destructive but must not be the loudest control in
         * its row", and the only way to de-emphasise one was to invent a styling path. That is what the
         * members bulk bar did: it separated Ban from Remove to calm it down and, by giving it the
         * solid variant and a full-width mobile box, made it the heaviest thing on screen instead.
         *
         * Use `destructive` when the destructive act IS the point of the surface (a delete
         * confirmation). Use `danger` when it sits beside ordinary actions and must read as available,
         * not as recommended.
         *
         * The EDGE is `--error-border`, the per-theme recipe the destructive notice surface uses, not an
         * alpha of `--destructive` (BL-135, 2026-09-15): that token never flips, its full-strength
         * ceiling on the dark card is 3.16:1, so `border-destructive/30` measured 1.63:1 light and
         * 1.26:1 dark — no boundary at all. The tint and the text token are unchanged.
         * `destructiveTokenContrast.spec.ts` measures the border against both surfaces in both themes.
         */
        case "danger":
            return "border-[var(--error-border)] bg-destructive/10 text-destructive-text shadow-none hover:bg-destructive/15"
        case "agree":
            return "border-transparent bg-agree/10 text-agree shadow-none hover:bg-agree/15"
        case "disagree":
            return "border-transparent bg-disagree/10 text-disagree shadow-none hover:bg-disagree/15"
        // `disabled:border-transparent` opts this one variant back out of the base class's disabled
        // hairline (see the block above `ctaBaseClass`). The link variant is inline TEXT, not a pill, so
        // an edge on it would draw a box around a word. It wins because `getCtaClasses` merges the
        // variant classes after the base ones and twMerge keeps the last of a conflicting pair.
        case "link":
            return "!h-auto min-w-0 !rounded-none border-transparent disabled:border-transparent !px-0 !py-0 bg-transparent text-primary shadow-none hover:bg-transparent hover:underline"
        case "ghost":
        default:
            return "border-transparent bg-transparent text-foreground shadow-none hover:bg-muted hover:text-foreground"
    }
}

/*
 * The MUTED tone of the ghost variant (SV-135 S3, design §5.3, D7).
 *
 * Content-action icons (reply, vote, share, save) rest in `text-muted-foreground` so the content, not the
 * chrome, is the loudest thing in a feed row. On hover or keyboard focus a control takes its action's own
 * semantic colour plus a 10% wash of the same colour. The semantic is read off the same class hooks the
 * ACTIVE map uses, so an agree control washes green and a disagree control red without a new prop.
 * The active states above are untouched and are `!important`, so a toggled vote or save still shows its
 * colour at rest (A5: accent marks state). Primary navigation (header compose, messages, bell) stays
 * `--foreground` and never takes this tone (D7).
 */
type CtaToneSemantic = "primary" | "agree" | "disagree"

const MUTED_TONE_CLASSES: Record<CtaToneSemantic, string> = {
    primary: "text-muted-foreground hover:bg-primary/10 hover:text-primary focus-visible:bg-primary/10 focus-visible:text-primary",
    agree: "text-muted-foreground hover:bg-agree/10 hover:text-agree focus-visible:bg-agree/10 focus-visible:text-agree",
    disagree: "text-muted-foreground hover:bg-disagree/10 hover:text-disagree focus-visible:bg-disagree/10 focus-visible:text-disagree",
}

function resolveToneSemantic(extraClass?: string | null): CtaToneSemantic {
    const mapped = getMappedActiveClass(extraClass)
    if (mapped === agreeActive) return "agree"
    if (mapped === disagreeActive) return "disagree"
    return "primary"
}

/**
 * The muted-tone classes for a control whose CtaButton / CtaToggle / VoteContainer only passes an
 * `extraClass` through (the discussion action row). Empty for the default tone. Append to the control's
 * own `extraClass`, which is merged last, so the tone wins over the ghost variant's resting text and wash.
 */
export function getCtaToneClass(tone: CtaTone | undefined, extraClass?: string | null): string {
    return tone === "muted" ? MUTED_TONE_CLASSES[resolveToneSemantic(extraClass)] : ""
}

export function getCtaClasses({
    size,
    variant,
    tone,
    roundedLeft,
    roundedRight,
    resolvedActiveClass,
    extraClass,
}: {
    size: CtaSize
    variant: CtaVariant
    tone?: CtaTone
    roundedLeft?: boolean
    roundedRight?: boolean
    resolvedActiveClass?: string
    extraClass?: string
}) {
    return cn(
        ctaBaseClass,
        getCtaSizePaddingClass(size),
        getCtaSizeHeightClass(size),
        // The link variant is inline text (!h-auto, min-w-0) — a 44px pill tap-floor would break it.
        variant === "link" ? "" : getCtaSizeTouchTargetClass(size),
        getCtaVariantClasses(variant),
        variant === "ghost" ? getCtaToneClass(tone, extraClass) : "",
        roundedLeft ? "rounded-l-full" : "",
        roundedRight ? "rounded-r-full" : "",
        !roundedLeft && !roundedRight ? "rounded-full" : "",
        resolvedActiveClass,
        extraClass,
    )
}
